import { TheSocietyType } from "../../static/types/commonTypes/theSocietyType"
import useValidation from "../../hooks/useValidation"
import { useEffect } from "react"
import FieldDescription from "../tools/FieldDescription"
import CapitalInput from "./CapitalInput"
import { CompanyNameType } from "../../static/types/commonTypes/companyNameType"
import companyNameValidationSchema from "../../utils/validation/companyNameValidationScehma"
import { validateSociety } from "../../utils/functions/validateTheSociety"
import useAppContext from "../../hooks/useAppContext"
import { AppContextType } from "../../context/AppContext"
import { useDraftManager } from "../../hooks/useDraftManager"

// Fonction utilitaire exportable pour être utilisée dans le composant de soumission
const formatObjectText = (text: string): string => {
  let formatted = text.replace(/\r\n/g, '\n');

  // 1. Remplacer une ou plusieurs sauts de ligne (avec espaces/tabs éventuels autour)
  formatted = formatted.replace(/[ \t]*\n+[ \t]*(?=\S)/g, '.-');

  // 2. Supprimer les sauts de ligne + espaces/tabs qui ne sont pas suivis de texte
  formatted = formatted.replace(/[ \t\r\n]+(?=\s*$)/g, '');

  // 3. Réduire les espaces/tabs multiples entre deux mots à un seul espace
  formatted = formatted.replace(/(\S)[ \t]+(?=\S)/g, '$1 ');

  // 4. Trim pour retirer éventuels blancs en début/fin
  formatted = formatted.trim();

  return formatted;
};



type managerProps = {
    checked: boolean,
    companyName: CompanyNameType,
    theSociety: TheSocietyType,
    societyType: string [],
    setTheSociety: ( theSociety: TheSocietyType ) => void,
    updateCompanyName: ( companyName: Partial<CompanyNameType> ) => void,
    updateTheSocietyValidity: ( isValid: boolean ) => void,
    updateCompanyNameValidity: ( isValid: boolean ) => void
}

export default function TheSociety({
    checked,
    theSociety,
    companyName,
    setTheSociety,
    societyType,
    updateCompanyName,
    updateTheSocietyValidity,
    updateCompanyNameValidity
}: managerProps) {

    const { draftFormData, setDraftFormData } = useAppContext() as AppContextType
    const { pushDraftContent } = useDraftManager()

    const updateEntityDetails = (details: Partial<TheSocietyType>) => {
        // Conserve la saisie telle quelle, sans formatage immédiat
        setTheSociety({ ...theSociety, ...details });
    };

    useEffect(() => {
        pushDraftContent({ theSociety })
    }, [ theSociety ])

    const { isValid, errors } = validateSociety(theSociety)
    const { isValid: isValidCompanyName, errors: localCompanyNameErros } = useValidation(companyName, companyNameValidationSchema)

    const LegalFormWithSCOPSA = [
        "Société coopérative de production à forme anonyme à Conseil d'Administration",
        "Société coopérative de production à forme anonyme à Directoire",
        "Société coopérative d'intérêt collectif à forme anonyme à Conseil d'Administration",
        "Société coopérative d'intérêt collectif à forme anonyme à Directoire",
        "Société coopérative de production à responsabilité limitée",
        "Société coopérative d'intérêt collectif à responsabilité limitée",
        "Société coopérative de production par actions simplifiée",
        "Société coopérative d'intérêt collectif par actions simplifiée"
    ];

    useEffect(() => {
        updateTheSocietyValidity(isValid)
    }, [isValid, updateTheSocietyValidity])

    useEffect(() => {
        updateCompanyNameValidity(isValidCompanyName)
    }, [isValid, updateCompanyNameValidity])

    return(
        <div className = "tab-content">

            <div className="mb-3 row">

                <div className="mb-3 row">

                    {/* Dénomination */}

                    <label htmlFor="companyName" className="col-sm-2 col-form-label"> Dénomination  <strong className="text-danger">*</strong>  </label>

                    <div className="col-sm-10">

                        <input
                            className={`decription-input form-control ${(checked && localCompanyNameErros.companyName) ? 'is-invalid' : ''}`}
                            type="text"
                            id="companyName"
                            value={companyName.companyName}
                            onChange={event => updateCompanyName({ companyName: event.target.value })}
                        />

                        <FieldDescription description="Nom de la société, doit être identique à celui de vos statuts." />
                        {(checked && localCompanyNameErros.companyName) && <div className="invalid-feedback"> {localCompanyNameErros.companyName} </div>}

                    </div>
                </div>

                {/* Sigle */}
                <div className="mb-3 row">
                    <label htmlFor="acronym" className="col-sm-2 col-form-label"> Sigle </label>

                    <div className="col-sm-10">

                        <input
                            className={`description-input form-control ${(checked && errors.acronym) ? 'is-invalid' : ''}`}
                            type="text"
                            id="acronym"
                            value={theSociety.acronym}
                            onChange={event => updateEntityDetails({ acronym: event.target.value })}
                        />

                        <FieldDescription description="Ensemble de lettres servant d'abréviation. A saisir uniquement si vous l'avez précisé dans vos statuts." />

                    </div>
                </div>

                {/* Forme juridique */}
                <div className="mb-3 row">
                    <label htmlFor="legalForm" className="col-sm-2 col-form-label"> Forme juridique  <strong className="text-danger">*</strong> </label>

                    <div className="col-sm-10">

                        <select
                            className = { `form-select ${ ( checked && errors.legalForm ) ? 'is-invalid' : ''}` }
                            id = "legalForm"
                            onChange = { e => updateEntityDetails({ legalForm: e.target.value }) }
                            value = { theSociety?.legalForm || "" }
                        >
                            <option value=""></option>
                            {
                                ( Array.isArray( societyType ) ? societyType : [ societyType ])
                                .map( ( option, key ) => 
                                    <option key = { key } value = { option }>
                                        { option }
                                    </option>
                                )
                            }
                        </select>

                        {  ( checked && errors.legalForm ) && <div className="invalid-feedback"> { errors.legalForm } </div> }

                    </div>
                </div>


                {/* Objet */}
                <div className="mb-3 row position-relative">

                    <label htmlFor="object" className="col-sm-2 col-form-label"> Objet  <strong className="text-danger">*</strong> </label>

                    <div className="col-sm-10">

                        <textarea
                            className={`description-input form-control ${(checked && errors.object) ? 'is-invalid' : ''}`}
                            id="object"
                            value={theSociety.object}
                            onChange={event => updateEntityDetails({ object: event.target.value })}
                            onBlur={event => {
                                const formattedValue = formatObjectText(event.target.value);
                                updateEntityDetails({ object: formattedValue });
                            }}
                        />

                        <FieldDescription description="Saisir l'activité principale de la société. Ne ps mentionner les activités annexes ou de type : Et, plus généralement, toutes opérations industrielles, commerciales, financières, mobilières ou immobilières..." />
                        {(checked && errors.object) && <div className="invalid-feedback"> {errors.object} </div>}

                    </div>
                </div>

                {
                    (theSociety.legalForm.includes("à capital variable") || LegalFormWithSCOPSA.includes(theSociety.legalForm))
                        ?
                        <>
                            <div className="mb-3 row position-relative">
                                <CapitalInput
                                    capital={theSociety?.minimalCapital || 0}
                                    checked={checked}
                                    errors={errors}
                                    fieldDescription="Pour les SARL et les SCI à capital variable, en plus du capital initial souscrit à la création, indiquez le capital minimum et le capital maximum, comme mentionnés dans les statuts. Le capital minimum ne peut pas être inférieur à 25% du capital initial."
                                    fieldKey="minimalCapital"
                                    inputLabel="Capital mini"
                                    onChange={updateEntityDetails}
                                />
                            </div>

                            <div className="mb-3 row position-relative">
                                <CapitalInput
                                    capital={theSociety?.initialCapital || 0}
                                    checked={checked}
                                    errors={errors}
                                    fieldDescription="Capital souscrit lors de la création. Par exemple : 1535,65"
                                    fieldKey="initialCapital"
                                    inputLabel="Capital initial"
                                    onChange={updateEntityDetails}
                                />
                            </div>
                            <div className="mb-3 row position-relative">
                                <CapitalInput
                                    capital={theSociety?.maximalCapital || 0}
                                    checked={checked}
                                    errors={errors}
                                    fieldDescription=""
                                    fieldKey="maximalCapital"
                                    inputLabel="Capital maxi"
                                    onChange={updateEntityDetails}
                                />
                            </div>
                        </>
                        :
                        <div className="mb-3 row position-relative">
                            <CapitalInput
                                capital={theSociety?.capital || 0}
                                checked={checked}
                                errors={errors}
                                fieldDescription="Montant du capital tel que mentionné dans les statuts. Par exemple : 1535,65"
                                fieldKey="capital"
                                inputLabel="Capital"
                                onChange={updateEntityDetails}
                            />
                        </div>
                }

                {/* Durée */}

                <div className="mb-3 row position-relative">

                    <label htmlFor="duration" className="col-sm-2 col-form-label"> Durée  <strong className="text-danger">*</strong> </label>

                    <div className="col-sm-10">

                    <input
                        className={`description-input form-control ${(checked && errors.duration) ? 'is-invalid' : ''}`}
                        id="duration"
                        value={theSociety.duration === 0 ? "" : theSociety.duration}
                        onChange={event => {
                            let value = event.target.value;
                            
                            value = value.replace(/[^0-9]/g, '');
                            
                            if (value && Number(value) < 1) {
                                value = "1";
                            }
                            if (value.length > 2) {
                                value = "99";
                            }
                            updateEntityDetails({ duration: Number(value) });
                        }}
                    />

                        <FieldDescription description="La durée de la société ne peut excéder 99 ans, elle pourra être prorogée une ou plusieurs fois." />
                        {(checked && errors.duration) && <div className="invalid-feedback"> {errors.duration} </div>}

                    </div>

                </div>

            </div>
        </div>
    )
}